import { useAppConfiguration } from "@/api/app-configuration.api";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { SegmentHeader } from "@/components/campaign/basic/SegmentHeader";
import { Button } from "@/components/generic/Button";
import { CheckCard } from "@/components/generic/CheckCard";
import { SettingsContainer } from "@/components/layout/SettingsContainer";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useMemo } from "react";
import { Form } from "react-aria-components";
import { useForm } from "react-hook-form";
import { z } from "zod";

export const Route = createFileRoute(
  "/_dashboard/campaigns/$id/(edit)/planning/triggers/$triggerId",
)({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    const trigger = context.appConfiguration.triggerTypes.find(
      (t) => t.event === params.triggerId,
    );

    return {
      showCampaignSetupSubStepper: false,
      showCampaignSetupBottomBar: false,
      crumb: trigger?.label,
      meta: {
        title: trigger?.label,
      },
    };
  },
});

const schema = z.object({
  enabled: z.boolean(),
});

function RouteComponent() {
  const { triggerId } = Route.useParams();
  const context = Route.useRouteContext();
  const { triggers, setTriggers } = context.useTriggers();
  const { data: appConfiguration } = useAppConfiguration();

  const trigger = useMemo(() => {
    const trigger = appConfiguration?.triggerTypes.find(
      (t) => t.event === triggerId,
    );
    if (!trigger) {
      throw new Error(`Trigger type ${triggerId} not found`);
    }
    return trigger;
  }, [appConfiguration, triggerId]);

  const navigate = useNavigate();

  const { control, handleSubmit } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      enabled: triggers.some((t) => t.event === triggerId),
    },
  });

  const submit = useMemo(
    () =>
      handleSubmit((data) => {
        setTriggers((triggers) =>
          data.enabled
            ? triggers.some((t) => t.event === triggerId)
              ? triggers
              : [...triggers, { event: triggerId }]
            : triggers.filter((t) => t.event !== triggerId),
        );

        navigate({
          from: Route.fullPath,
          to: "..",
        });
      }),
    [handleSubmit, navigate, setTriggers, triggerId],
  );

  return (
    <Form className="size-full" onSubmit={submit}>
      <SettingsContainer className="size-full">
        <div className="flex h-full flex-col gap-y-10">
          <div className="flex items-center justify-between">
            <Breadcrumbs size="sm" from="/campaigns/$id/planning/triggers" />

            <Route.Link to="..">
              <XIcon className="size-5" />
            </Route.Link>
          </div>

          <div className="flex size-full flex-col items-center justify-center gap-y-12">
            <div className="flex flex-col items-center gap-y-10">
              <SegmentHeader
                title={trigger.label}
                description={trigger.description}
              />

              <CheckCard
                className="w-auto"
                control={control}
                name="enabled"
                label="Aktiver"
              />
            </div>

            <Button size="lg" type="submit" animate>
              Gem
            </Button>
          </div>
        </div>
      </SettingsContainer>
    </Form>
  );
}
